/**
 * CadenceFormedUpTab — Tier 2 (Formed-up Cadence) viewer.
 *
 * Runs the formed-up engine over a date window (start + watch days), optionally
 * scoped to one cadence rule, committing tentative occurrences in that window to
 * the real calendar (SCH_event_instances) and calling each use-case's underlying
 * action. Shows the resulting firm calendar/register rows. Re-runnable for test.
 *
 * Server-paginated: the firm fire-log is read one page at a time (LIMIT/OFFSET +
 * COUNT-backed total) via listFiresPaged; the rule picker still pulls the full
 * rule set via getRules().
 */
import React, { useEffect, useState } from 'react';
import { Play, Trash2, RefreshCw } from 'lucide-react';
import Button from '../common/Button';
import FormControl from '../common/FormControl';
import Pagination from '../common/Pagination';
import Skeleton from '../common/Skeleton';
import NoDataFound from '../common/NoDataFound';
import Badge from '../common/Badge';
import Toast, { ToastMessage } from '../common/Toast';
import { confirmWarning, swalPermission } from '../common/confirm';
import { statusVariant } from '../../utils/statusVariant';
import extractApiError from '../../utils/apiError';
import {
  listFiresPaged, runFormup, purgeFires, getRules,
  TentativeFire, CadenceRule,
} from '../../services/cadenceService';
import { usePermissions } from '../../contexts/PermissionContext';
import { useEntitySettings } from '../../contexts/EntitySettingsContext';
import { SCH_GLOBAL_CADENCE } from './cadenceModuleTypes';

const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const CadenceFormedUpTab: React.FC = () => {
  const { formatDate } = useEntitySettings();
  // Standalone Cadence Engine → global cadence activity. Run formed-up / clear
  // are mutations gated by _enforce_cadence_modify on the backend.
  const { check, loading: permLoading } = usePermissions();
  const canModify = check(SCH_GLOBAL_CADENCE, 'view+modify').allowed;
  const canView = check(SCH_GLOBAL_CADENCE, 'view').allowed;
  const [rows, setRows] = useState<TentativeFire[]>([]);
  const [rules, setRules] = useState<CadenceRule[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [start, setStart] = useState<string>(todayISO());
  const [windowDays, setWindowDays] = useState<number>(15);
  const [ruleId, setRuleId] = useState<number | ''>('');
  const [reloadNonce, setReloadNonce] = useState(0);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  useEffect(() => { setPage(1); }, [ruleId, pageSize]);

  useEffect(() => {
    if (!canView) return;
    let cancelled = false;
    setLoading(true);
    listFiresPaged('formed', { page, limit: pageSize, ruleId: ruleId === '' ? undefined : ruleId })
      .then(res => {
        if (cancelled) return;
        setRows(res.items);
        setTotalRecords(res.total);
      })
      .catch(err => {
        if (cancelled) return;
        setRows([]);
        setTotalRecords(0);
        setToast({ type: 'error', message: extractApiError(err, 'Failed to load formed-up rows.') });
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, pageSize, ruleId, reloadNonce, canView]);

  useEffect(() => { getRules().then(setRules).catch(() => setRules([])); }, []);

  const refresh = () => setReloadNonce(n => n + 1);
  const reloadFirstPage = () => { setPage(1); setReloadNonce(n => n + 1); };

  const handleRun = async () => {
    if (!canModify) { swalPermission('You do not have permission to run cadence.'); return; }
    if (!start) { setToast({ type: 'error', message: 'Pick a start date.' }); return; }
    setBusy(true);
    try {
      const r = await runFormup(start, windowDays, ruleId === '' ? undefined : ruleId);
      const acts = Object.entries(r.actions_dispatched || {}).map(([k, v]) => `${k}:${v}`).join(', ') || 'none';
      setToast({ type: 'success', message: `Window ${r.window.start}…${r.window.end}: ${r.calendar_committed} committed (of ${r.occurrences_in_window} in window). Actions → ${acts}.` });
      reloadFirstPage();
    } catch (err: any) {
      setToast({ type: 'error', message: extractApiError(err, 'Formed-up run failed.') });
    } finally {
      setBusy(false);
    }
  };

  const handleClear = async () => {
    if (!canModify) { swalPermission('You do not have permission to modify cadence.'); return; }
    const ok = await confirmWarning({
      title: 'Clear formed-up calendar?',
      text: 'Deletes the formed-up fire-log rows for this entity. Calendar obligations (SCH_event_instances) are not removed here.',
      confirmText: 'Yes, Clear',
    });
    if (!ok) return;
    setBusy(true);
    try {
      const r = await purgeFires('formed', ruleId === '' ? undefined : ruleId);
      setToast({ type: 'success', message: `Deleted ${r.fire_log_deleted} formed-up row(s).` });
      reloadFirstPage();
    } catch (err: any) {
      setToast({ type: 'error', message: extractApiError(err, 'Clear failed.') });
    } finally {
      setBusy(false);
    }
  };

  const ruleLabel = (r: CadenceRule) => `#${r.id} — ${r.obligation_name || r.source_display_name || 'rule'}`;

  if (!permLoading && !canView) {
    return (
      <div className="flex flex-col h-full min-h-[300px] items-center justify-center">
        <NoDataFound title="Not authorised" message="You do not have access to cadence." />
      </div>
    );
  }

  return (
    <div className="netbody-card flex flex-col h-full min-h-[500px]">
      <Toast toast={toast} onClose={() => setToast(null)} />

      <div className="netbody-header">
        <div className="flex items-center gap-2 ml-auto">
          <div style={{ width: 150, flexShrink: 0 }}>
            <FormControl.DatePicker
              id="formup_start"
              value={start}
              onChange={e => setStart(e.target.value)}
              data-testid="INP_formup_start"
              noMargin
            />
          </div>
          <div style={{ width: 110, flexShrink: 0 }}>
            <FormControl.Select
              value={String(windowDays)}
              onChange={e => setWindowDays(parseInt(e.target.value, 10))}
              data-testid="SEL_formup_window"
            >
              <option value="7">7 days</option>
              <option value="15">15 days</option>
              <option value="30">30 days</option>
              <option value="60">60 days</option>
              <option value="90">90 days</option>
            </FormControl.Select>
          </div>
          <div style={{ width: 240, flexShrink: 0 }}>
            <FormControl.SearchSelect
              value={ruleId === '' ? null : ruleId}
              onChange={v => setRuleId(v === null || v === '' ? '' : Number(v))}
              options={rules.map(r => ({ value: r.id, label: ruleLabel(r) }))}
              placeholder="All rules"
              clearable
              noMargin
              data-testid="SEL_formup_rule"
            />
          </div>

          <Button variant="secondary-outline" icon={RefreshCw} onClick={refresh} data-testid="BTN_formup_refresh">
            Refresh
          </Button>
          <Button variant="delete-outline" icon={Trash2} disabled={busy} onClick={handleClear} className={!canModify ? 'opacity-50 cursor-not-allowed' : ''} data-testid="BTN_formup_clear">
            Clear Formed-up
          </Button>
          <Button variant="fill" icon={Play} disabled={busy} onClick={handleRun} className={!canModify ? 'opacity-50 cursor-not-allowed' : ''} data-testid="BTN_formup_run">
            {busy ? 'Running…' : 'Run Formed-up'}
          </Button>
        </div>
      </div>

      <div className="netbody-content">
        <div className="scroll-container flex-1 overflow-hidden">
          <div className="table-list">
            <div className="table-list-scroll">
              <table className="table-list-table">
                <thead>
                  <tr>
                    <th className="table-list-th table-list-cell--sno">S.No</th>
                    <th className="table-list-th">Rule</th>
                    <th className="table-list-th">Occurrence</th>
                    <th className="table-list-th">Due date</th>
                    <th className="table-list-th">Geography</th>
                    <th className="table-list-th">Authority</th>
                    <th className="table-list-th table-list-cell--status">Status</th>
                  </tr>
                </thead>
                <tbody aria-busy={loading} aria-live="polite">
                  {loading && (
                    <Skeleton.TableRows cols={[{ width: '3xs', align: 'center' }, 'sm', '2xs', 'sm', 'md', 'md', 'pill']} />
                  )}

                  {!loading && rows.length === 0 && (
                    <tr>
                      <td colSpan={7} className="table-list-cell">
                        <NoDataFound
                          title="Nothing formed up yet"
                          message="Pick a start date + window and run formed-up to commit the tentative occurrences in that window to the calendar."
                        />
                      </td>
                    </tr>
                  )}

                  {!loading && rows.map((f, idx) => (
                    <tr key={f.id} className="table-list-row" data-testid={`ROW_formed_${f.id}`}>
                      <td className="table-list-cell table-list-cell--sno">{(page - 1) * pageSize + idx + 1}</td>
                      <td className="table-list-cell">#{f.rule_id}</td>
                      <td className="table-list-cell">{f.occurrence_number}</td>
                      <td className="table-list-cell">{formatDate(f.scheduled_for)}</td>
                      <td className="table-list-cell">{f.geography_name || `loc ${f.scope_element_id ?? '—'}`}</td>
                      <td className="table-list-cell">{f.authority_name || (f.authority_id ? `auth ${f.authority_id}` : '—')}</td>
                      <td className="table-list-cell table-list-cell--status">
                        <Badge variant={statusVariant(f.status)} size="sm">{f.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <Pagination
              page={page}
              pageSize={pageSize}
              totalRecords={totalRecords}
              onPageChange={setPage}
              onPageSizeChange={setPageSize}
              recordLabel="formed-up obligations"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default CadenceFormedUpTab;
